import React, { forwardRef } from 'react';
import { Checkbox, FormControlLabel, SxProps, Typography, styled } from '@mui/material';
export interface TwCheckboxProps {
  /**
   * The id of the input element.
   */
  id?: string;
  /**
   * Label text for the checkbox.
   */
  label: string;
  /**
   * Checkbox editable status.
   */
  disabled?: boolean;
  /**
   * Checked value
   */
  checked?: boolean;
  /**
   * Checkbox value
   */
  value?: boolean | string;
  /**
   * Name of checkbox
   */
  name: string;
  /**
   * Callback fired when the value is changed.
   */
  onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Default checked value
   */
  defaultChecked?: boolean;
  /**
   * MUI sx props
   */
  sx?: SxProps;
  /**
   * MUI checkbox sx props
   */
  sxCheckbox?: SxProps;
  /**
   * indeterminate value, third state for checkbox
   */
  indeterminate?: boolean;
}

interface StyledLabelCheckboxProps {
  disabled?: boolean;
}

const StyledCheckboxLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'disabled'
})<StyledLabelCheckboxProps>(({ disabled }) => ({
  color: disabled ? '#7E858E' : '#171e28',
  fontSize: '14px',
  fontFamily: 'Poppins',
  whiteSpace: 'nowrap'
}));

const StyledCheckbox = styled(Checkbox)({
  fontSize: '14px',
  fontFamily: 'Poppins',
  color: '#E2E2EA',
  marginRight: 0,

  '&.Mui-checked, .MuiCheckbox-indeterminate': {
    color: '#3959df'
  },
  '&.Mui-disabled': {
    color: '#E2E2EA'
  }
});

const StyledFormControlLabel = styled(FormControlLabel)({
  fontSize: '14px',
  fontFamily: 'Poppins',
  color: '#E2E2EA',
  marginRight: 0,

  '&.Mui-checked, .MuiCheckbox-indeterminate': {
    color: '#3959df !important'
  },
  '&.Mui-disabled': {
    color: '#E2E2EA'
  }
});

const TwCheckbox = forwardRef<HTMLLabelElement, TwCheckboxProps>(
  (
    {
      id,
      label,
      name,
      disabled,
      value,
      onInputChange,
      checked,
      defaultChecked,
      sx,
      sxCheckbox,
      indeterminate,
      ...restProps
    },
    ref
  ) => {
    return (
      <StyledFormControlLabel
        ref={ref}
        className="tid-checkbox"
        control={
          <StyledCheckbox
            name={name}
            onChange={onInputChange}
            value={value}
            disabled={disabled}
            id={id}
            checked={checked}
            defaultChecked={defaultChecked}
            sx={sxCheckbox}
            indeterminate={indeterminate}
          />
        }
        label={<StyledCheckboxLabel>{label}</StyledCheckboxLabel>}
        sx={sx}
        {...restProps}
      />
    );
  }
);

TwCheckbox.defaultProps = {
  disabled: false
};

TwCheckbox.displayName = 'TWCheckbox';

export default TwCheckbox;
